import { projectId, publicAnonKey } from '../utils/supabase/info';

const API_BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-aa94c070`;

// Helper: headers for PUBLIC endpoints (no user auth needed)
const publicHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${publicAnonKey}`,
});

// Helper: headers for PROTECTED endpoints.
// Authorization carries the publicAnonKey so the Supabase gateway accepts the
// request (it only validates HS256 anon/service keys at gateway level).
// The user's ES256 access_token travels in X-Admin-Token and is verified
// inside the Edge Function by the requireAuth middleware.
const protectedHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${publicAnonKey}`,
  'X-Admin-Token': token,
});

// ============================================================================
// 🔐 AUTHENTICATION SERVICE
// ============================================================================

export interface LoginResponse {
  success: boolean;
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export const adminLogin = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}/admin/login`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.detail || errorBody.error || 'Login failed';
    console.error('📡 [API] adminLogin error:', response.status, errorBody);
    throw new Error(message);
  }

  return response.json();
};

export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/verify`, {
      method: 'POST',
      headers: protectedHeaders(token),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.valid === true;
  } catch (error) {
    console.error('Token verification error:', error);
    return false;
  }
};

// ============================================================================
// 📊 RISK PROFILE SERVICE
// ============================================================================

export interface RiskProfileData {
  name: string;
  email: string;
  language: string;
  answers: Record<string, any>;
  totalScore: number;
  allocation: Record<string, number>;
  riskLevel: string;
}

export interface RiskProfile extends RiskProfileData {
  id: string;
  createdAt: string;
}

export interface SaveRiskProfileResponse {
  success: boolean;
  id: string;
  message: string;
}

export const saveRiskProfile = async (profileData: RiskProfileData): Promise<SaveRiskProfileResponse> => {
  const response = await fetch(`${API_BASE_URL}/risk-profiles`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify(profileData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save risk profile');
  }

  return response.json();
};

export interface GetRiskProfilesResponse {
  success: boolean;
  data: RiskProfile[];
  count: number;
}

export const getRiskProfiles = async (token: string): Promise<GetRiskProfilesResponse> => {
  console.log('📡 [API] getRiskProfiles called');
  console.log('📡 [API] Token received:', token ? `Yes (${token.length} chars)` : 'No');
  console.log('📡 [API] Token preview:', token?.substring(0, 30) + '...');
  console.log('📡 [API] Calling:', `${API_BASE_URL}/risk-profiles`);
  
  const response = await fetch(`${API_BASE_URL}/risk-profiles`, {
    method: 'GET',
    headers: protectedHeaders(token),
  });

  console.log('📡 [API] Response status:', response.status);
  console.log('📡 [API] Response ok:', response.ok);

  if (!response.ok) {
    const error = await response.json();
    console.error('📡 [API] Error response:', error);
    throw new Error(error.error || 'Failed to retrieve risk profiles');
  }

  const data = await response.json();
  console.log('📡 [API] Success! Profiles count:', data.count);
  return data;
};

export interface DeleteRiskProfileResponse {
  success: boolean;
  message: string;
}

export const deleteRiskProfile = async (id: string, token: string): Promise<DeleteRiskProfileResponse> => {
  const response = await fetch(`${API_BASE_URL}/risk-profiles/${id}`, {
    method: 'DELETE',
    headers: protectedHeaders(token),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete risk profile');
  }

  return response.json();
};

// ============================================================================
// 🏥 HEALTH CHECK
// ============================================================================

export const checkHealth = async (): Promise<{ status: string; timestamp: string }> => {
  const response = await fetch(`${API_BASE_URL}/health`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${publicAnonKey}`,
    },
  });

  if (!response.ok) {
    throw new Error('Health check failed');
  }

  return response.json();
};

// ============================================================================
// 📄 PDF STORAGE
// ============================================================================

export interface UploadPdfResponse {
  success: boolean;
  pdfPath: string;
  message: string;
}

export const uploadPdf = async (profileId: string, pdfBase64: string, fileName?: string): Promise<UploadPdfResponse> => {
  const response = await fetch(`${API_BASE_URL}/risk-profiles/${profileId}/pdf`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify({ pdfBase64, fileName }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload PDF');
  }

  return response.json();
};

export interface GetPdfUrlResponse {
  success: boolean;
  url: string;
  expiresIn: number;
}

export const getPdfUrl = async (profileId: string, token: string): Promise<GetPdfUrlResponse> => {
  const response = await fetch(`${API_BASE_URL}/risk-profiles/${profileId}/pdf`, {
    method: 'GET',
    headers: protectedHeaders(token),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get PDF URL');
  }

  return response.json();
};